// Your account (#804): what is the person's own rather than the workspace's, reached
// from the avatar menu. Settings was one page that mixed the two; the workspace's
// sections are `SettingsPage`'s, each at its own path.
//
// Each panel reads for itself. The factors, the password and the sessions are the
// person's, in every workspace; the API keys are the selected workspace's (#807), so
// that panel is drawn only in a workspace, to its own people whose role may read them (#1002),
// and offers writes only to a role that may make them.
// An impersonated session manages none of them, because they belong to the person
// signed in and not to whoever is viewing as them (#658, #746, #807).

import type { ApiKeyActions, SessionActions } from "../api/account";
import type { AuthenticatorActions } from "../api/authenticator";
import type { PasskeyActions } from "../api/passkeys";
import { roleHolds, type Role } from "../api/roles";
import type { SessionResponse } from "../api/session";
import { YourApiKeys } from "./ApiKeys";
import { YourPassword } from "./ChangePassword";
import { YourDeletion, type DeletionRequestActions } from "./DeleteAccount";
import { Notice, PageIntro } from "./chrome";
import { YourPasskeys } from "./Passkeys";
import { YourAuthenticator } from "./ReplaceAuthenticator";
import { YourSessions } from "./Sessions";

export function AccountPage({
  session,
  passkeys,
  authenticator,
  apiKeys,
  sessions,
  changePassword,
  deletion,
  roles = null,
}: {
  session: SessionResponse;
  /** `GET /auth/roles`, which says whether this session may manage API keys; null unread. */
  roles?: Role[] | null;
  /** The signed-in user's passkeys (#660); absent, the panel is not drawn. */
  passkeys?: PasskeyActions;
  /** The signed-in user's authenticator app (#746); absent, the panel is not drawn. */
  authenticator?: AuthenticatorActions;
  /** The workspace's API keys (#807); absent, the panel is not drawn. */
  apiKeys?: ApiKeyActions;
  /** The signed-in user's sessions (#807); absent, the panel is not drawn. */
  sessions?: SessionActions;
  /** Change the signed-in user's password (#807); absent, the panel is not drawn. */
  changePassword?: (currentPassword: string, newPassword: string) => Promise<void>;
  /** Ask for the signed-in user's account to be deleted (#1136); absent, the panel is not drawn. */
  deletion?: DeletionRequestActions;
}) {
  const own = session.impersonated_by === null;
  const currentId = session.session?.session_id;
  // `GET /auth/api-keys` answers the workspace's own people whose role holds `api_keys:read`,
  // and never a visiting organisation (#1002); elsewhere the panel would only show its refusal.
  const mayReadKeys = !session.acting_org_id && roleHolds(roles, session.role, "api_keys:read");
  return (
    <>
      <PageIntro destination="account" />
      {own ? null : (
        <Notice>
          Passkeys, the authenticator app, the password, sessions, API keys and deleting the
          account are managed only by the person signed in, so they are not shown while you view
          as this user.
        </Notice>
      )}
      {own && passkeys !== undefined ? <YourPasskeys actions={passkeys} /> : null}
      {own && authenticator !== undefined ? <YourAuthenticator actions={authenticator} /> : null}
      {own && changePassword !== undefined ? (
        <YourPassword passwordSet={session.user.password_set} onChange={changePassword} />
      ) : null}
      {own && sessions !== undefined ? (
        <YourSessions
          actions={sessions}
          currentId={typeof currentId === "string" ? currentId : null}
        />
      ) : null}
      {own && apiKeys !== undefined && session.workspace_id !== null && mayReadKeys ? (
        <YourApiKeys
          actions={apiKeys}
          canManage={roleHolds(roles, session.role, "api_keys:manage")}
        />
      ) : null}
      {own && deletion !== undefined ? <YourDeletion actions={deletion} /> : null}
    </>
  );
}
